import { Fragment } from 'react';
import { Route, Routes } from 'react-router-dom';

import { Fade, styled, useTheme } from '@mui/material';

import { PermissionGuard } from 'permissions';
import {
    functionalComponentPropType,
    renderedComponentPropType
} from '@libs/propTypes';

import Employee from 'employee/Employee';

import CreateRole from 'roles/Roles';
import { User } from 'user/User';
import { useNavigationState } from '../useNavigation';
import { useContentStyle, useFooterRef, useRenderAll } from './hooks';
import { LeaveCategoryCreateUpdate } from 'leaveCategory/LeaveCategory';
// import LeaveDetails from 'leaveManagement/LeaveManagement/LeaveDetails';
import LeaveDetails from 'common/components/leaveDetails';
import FileViewer from 'common/components/FileViewer';
import ManagerialTree from 'managerialTree';
import Holiday from 'holidays/holidays';
import ManageEmployee from 'employee/ManageEmployee';
import Profile from 'profile';
import ChangeRequestPage from 'profileChangeRequests/components/ChangeRequestPage';
import TeamDetailsPage from 'teamManagement/components/TeamDetailsPage';
import ClientDetailsPage from 'clientManagement/components/ClientDetailsPage';

const propTypes = {
    FooterComponent: renderedComponentPropType,
    NotFoundPageComponent: functionalComponentPropType,
    EmptyRoutePage: functionalComponentPropType
};

const defaultProps = {
    FooterComponent: Fragment,
    NotFoundPage: () => <Fragment />,
    EmptyRoutePage: () => <div />
};

const Main = styled('main')(({ theme: { palette, spacing, transitions } }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    overflow: 'hidden',

    minHeight: '100vh',
    paddingLeft: 'var(--contentPaddingLeft)',
    paddingTop: `calc(var(--contentPaddingTop) + ${spacing(4)})`,

    backgroundColor: palette.background.main,

    transition: transitions.create('padding', {
        easing: transitions.easing.sharp,
        duration: transitions.duration.shorter
    })
}));
const StyledContent = styled('div')(({ theme: { spacing } }) => ({
    paddingLeft: spacing(4),
    paddingRight: spacing(4),
    '@media screen and (max-width: 768px)': {
        paddingLeft: spacing(3),
        paddingRight: spacing(3)
    }
}));

const Content = ({ FooterComponent, NotFoundPage, EmptyRoutePage }) => {
    const { transitions } = useTheme();
    const { flatRoutes } = useNavigationState();

    return (
        <Main style={useContentStyle()}>
            <Fade
                in={true}
                timeout={{
                    enter: transitions.duration.enteringScreen,
                    exit: transitions.duration.leavingScreen
                }}
            >
                <StyledContent>
                    <Routes>
                        {/* Block exact /employee-list — show 404 */}
                        <Route path='/employee-list' element={<NotFoundPage />} />

                        {flatRoutes.map(
                            ({
                                Component = EmptyRoutePage,
                                route,
                                name,
                                parents,
                                childRoutes
                            }) => (
                                <Route
                                    key={route}
                                    exact
                                    path={route}
                                    element={
                                        <Component
                                            name={name}
                                            parents={parents}
                                            childRoutes={childRoutes}
                                        />
                                    }
                                />
                            )
                        )}

                        <Route
                            path='/employee-list/*'
                            element={
                                <PermissionGuard requires="employee.update an employee" fallback={<NotFoundPage />}>
                                    <Employee mode='update' />
                                </PermissionGuard>
                            }
                        />
                        <Route
                            path='/manage/*'
                            element={<ManageEmployee mode='update' />}
                        />
                        <Route
                            path='/user-list/update/*'
                            element={<User mode='update' />}
                        />
                        <Route
                            path='/holiday-list/update/*'
                            element={<Holiday mode='update' />}
                        />
                        <Route
                            path='/role-list/update/*'
                            element={<CreateRole mode='update' />}
                        />
                        <Route
                            path='/leave-category/update/*'
                            element={
                                <LeaveCategoryCreateUpdate mode='update' />
                            }
                        />
                        <Route
                            path='/leave-details/*'
                            element={<LeaveDetails />}
                        />
                        <Route path='/file-viewer' element={<FileViewer />} />
                        <Route path='/my-profile/*' element={<Profile />} />
                        <Route
                            path='/managerial-tree/*'
                            element={<ManagerialTree />}
                        />
                        <Route path='/profile/*' element={<Profile />} />
                        <Route
                            path='/profile-change-request'
                            element={<ChangeRequestPage />}
                        />
                        <Route path='/teams/:id' element={<TeamDetailsPage />} />
                        <Route path='/clients/:id' element={<ClientDetailsPage />} />
                        <Route path='*' element={useRenderAll(NotFoundPage)} />
                    </Routes>
                </StyledContent>
            </Fade>
            <FooterComponent ref={useFooterRef()} />
        </Main>
    );
};

Content.propTypes = propTypes;
Content.defaultProps = defaultProps;

export default Content;
